import React, { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Loader2, Upload, X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import { diamondsService, ItemCategoria } from '@/services/diamonds.service';

const novoItemSchema = z.object({
  nome: z.string().min(1, 'Nome obrigatório'),
  descricao: z.string().min(1, 'Descrição obrigatória'),
  categoria_id: z.coerce.number({ invalid_type_error: 'Categoria obrigatória' })
    .int().positive('Categoria obrigatória'),
  valor: z.coerce.number({ invalid_type_error: 'Valor obrigatório' })
    .int('Use número inteiro').min(1, 'Valor mínimo é 1'),
  qntd: z.coerce.number({ invalid_type_error: 'Quantidade obrigatória' })
    .int('Use número inteiro').min(1, 'Quantidade mínima é 1'),
  custo: z.coerce.number({ invalid_type_error: 'Custo obrigatório' })
    .min(0, 'Custo deve ser >= 0'),
  imagem: z.string().min(1, 'Imagem principal obrigatória'),
  fileType: z.string().min(1),
  imagem_adicional_1: z.string().optional(),
  fileType_adicional_1: z.string().optional(),
  imagem_adicional_2: z.string().optional(),
  fileType_adicional_2: z.string().optional(),
});

type NovoItemForm = z.infer<typeof novoItemSchema>;

const fileToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

interface NovoItemDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: () => void;
}

export default function NovoItemDialog({ open, onOpenChange, onCreated }: NovoItemDialogProps) {
  const [categorias, setCategorias] = useState<ItemCategoria[]>([]);
  const [isLoadingCategorias, setIsLoadingCategorias] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    control,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<NovoItemForm>({
    resolver: zodResolver(novoItemSchema),
    mode: 'onChange',
    defaultValues: {
      nome: '',
      descricao: '',
      valor: 0,
      qntd: 0,
      custo: 0,
      imagem: '',
      fileType: '',
    },
  });

  const imagem = watch('imagem');
  const imagemAdicional1 = watch('imagem_adicional_1');
  const imagemAdicional2 = watch('imagem_adicional_2');

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const loadCategorias = async () => {
      setIsLoadingCategorias(true);
      try {
        const response: any = await diamondsService.getItemsCategories();
        if (!cancelled) {
          setCategorias(response?.data ?? []);
        }
      } catch (error) {
        console.error('Erro ao buscar categorias:', error);
        if (!cancelled) toast.error('Erro ao carregar categorias');
      } finally {
        if (!cancelled) setIsLoadingCategorias(false);
      }
    };
    loadCategorias();
    return () => { cancelled = true; };
  }, [open]);

  useEffect(() => {
    if (!open) reset();
  }, [open, reset]);

  const handleImagemPrincipal = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const base64 = await fileToBase64(file);
      setValue('imagem', base64, { shouldValidate: true });
      setValue('fileType', file.type, { shouldValidate: true });
    } catch {
      toast.error('Erro ao processar imagem');
    }
  };

  const handleImagemAdicional = async (
    e: React.ChangeEvent<HTMLInputElement>,
    index: 1 | 2,
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const base64 = await fileToBase64(file);
      if (index === 1) {
        setValue('imagem_adicional_1', base64);
        setValue('fileType_adicional_1', file.type);
      } else {
        setValue('imagem_adicional_2', base64);
        setValue('fileType_adicional_2', file.type);
      }
    } catch {
      toast.error('Erro ao processar imagem');
    }
  };

  const removeAdicional = (index: 1 | 2) => {
    if (index === 1) {
      setValue('imagem_adicional_1', undefined);
      setValue('fileType_adicional_1', undefined);
    } else {
      setValue('imagem_adicional_2', undefined);
      setValue('fileType_adicional_2', undefined);
    }
  };

  const onSubmit = async (values: NovoItemForm) => {
    setIsSubmitting(true);
    try {
      await diamondsService.createItem(values);
      toast.success('Item criado com sucesso!');
      reset();
      onOpenChange(false);
      onCreated();
    } catch (error) {
      console.error('Erro ao criar item:', error);
      toast.error('Erro ao criar item');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Novo Item</DialogTitle>
        </DialogHeader>

        <ScrollArea className="max-h-[65vh] pr-4">
          <form id="novo-item-form" onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div>
              <Label htmlFor="nome">Nome *</Label>
              <Input id="nome" {...register('nome')} />
              {errors.nome && <p className="text-sm text-destructive mt-1">{errors.nome.message}</p>}
            </div>

            <div>
              <Label htmlFor="descricao">Descrição *</Label>
              <Textarea id="descricao" rows={2} {...register('descricao')} />
              {errors.descricao && <p className="text-sm text-destructive mt-1">{errors.descricao.message}</p>}
            </div>

            <div>
              <Label htmlFor="categoria_id">Categoria *</Label>
              <Controller
                name="categoria_id"
                control={control}
                render={({ field }) => (
                  <Select
                    onValueChange={(v) => field.onChange(Number(v))}
                    value={field.value ? String(field.value) : ''}
                    disabled={isLoadingCategorias}
                  >
                    <SelectTrigger id="categoria_id">
                      <SelectValue placeholder={isLoadingCategorias ? 'Carregando...' : 'Selecione'} />
                    </SelectTrigger>
                    <SelectContent>
                      {categorias.map((cat) => (
                        <SelectItem key={cat.id} value={String(cat.id)}>
                          {cat.nome}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
              {errors.categoria_id && <p className="text-sm text-destructive mt-1">{errors.categoria_id.message}</p>}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label htmlFor="valor">Valor (Diamantes) *</Label>
                <Input id="valor" type="number" min={1} step={1} {...register('valor')} />
                {errors.valor && <p className="text-sm text-destructive mt-1">{errors.valor.message}</p>}
              </div>
              <div>
                <Label htmlFor="qntd">Quantidade *</Label>
                <Input id="qntd" type="number" min={1} step={1} {...register('qntd')} />
                {errors.qntd && <p className="text-sm text-destructive mt-1">{errors.qntd.message}</p>}
              </div>
              <div>
                <Label htmlFor="custo">Custo (R$) *</Label>
                <Input id="custo" type="number" min={0} step={0.01} {...register('custo')} />
                {errors.custo && <p className="text-sm text-destructive mt-1">{errors.custo.message}</p>}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <ImageSlot
                label="Imagem Principal *"
                preview={imagem}
                onChange={handleImagemPrincipal}
                error={errors.imagem?.message}
              />
              <ImageSlot
                label="Imagem Adicional 1"
                preview={imagemAdicional1}
                onChange={(e) => handleImagemAdicional(e, 1)}
                onRemove={imagemAdicional1 ? () => removeAdicional(1) : undefined}
              />
              <ImageSlot
                label="Imagem Adicional 2"
                preview={imagemAdicional2}
                onChange={(e) => handleImagemAdicional(e, 2)}
                onRemove={imagemAdicional2 ? () => removeAdicional(2) : undefined}
              />
            </div>
          </form>
        </ScrollArea>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            Cancelar
          </Button>
          <Button type="submit" form="novo-item-form" disabled={isSubmitting || !imagem}>
            {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Adicionar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface ImageSlotProps {
  label: string;
  preview?: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onRemove?: () => void;
  error?: string;
}

function ImageSlot({ label, preview, onChange, onRemove, error }: ImageSlotProps) {
  return (
    <div className="border rounded-md p-2 flex flex-col items-center gap-2">
      <Label className="text-xs text-center">{label}</Label>
      <div className="w-full aspect-square bg-muted rounded flex items-center justify-center overflow-hidden">
        {preview ? (
          <img src={preview} alt={label} className="w-full h-full object-contain" />
        ) : (
          <Upload className="h-6 w-6 text-muted-foreground" />
        )}
      </div>
      <Input type="file" accept="image/*" onChange={onChange} className="text-xs" />
      {onRemove && (
        <Button type="button" variant="ghost" size="sm" onClick={onRemove} className="text-destructive h-7">
          <X className="h-3 w-3 mr-1" /> Remover
        </Button>
      )}
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}
